import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import React, { useState } from 'react'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { Truncate } from '@ui/Truncate/Truncate'
import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { TextColorType } from '@ui/design-system/colors/colors'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { Box } from '../Box'
import { MenuSection } from './menuSections/useMenuSections'

import styles from './MenuLink.module.scss'

type MenuLinkProps = {
  section: MenuSection
  isActive?: boolean
  isExpanded?: boolean
  isVisible?: boolean
  onClick?: (e: React.MouseEvent) => void
  onMouseEnter?: (e: React.MouseEvent) => void
  onMouseLeave?: (e: React.MouseEvent) => void
  infoClick?: () => void
  chevronDirection?: 'right' | 'down'
  className?: string
  showChevron?: boolean
  buttonRef?: React.Ref<HTMLButtonElement>
}

const iconVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

export const MenuLink: React.FC<MenuLinkProps> = (props) => {
  const {
    isActive,
    section,
    onClick,
    onMouseEnter,
    onMouseLeave,
    infoClick,
    chevronDirection = 'right',
    showChevron,
    className,
    buttonRef,
  } = props
  const [hovered, setHovered] = useState(false)

  const {
    icon: Icon,
    rightIcon: RightIcon,
    label,
    highlighted,
    alertActive,
    isSelected,
    screenChildren,
  } = section

  const handleButtonClick = (event: React.MouseEvent) => {
    setHovered(false)
    onClick?.(event)
  }

  const active = isActive || isSelected
  const iconColor: TextColorType = highlighted && hovered ? 'text-med-em' : 'text-white'

  return (
    <motion.button
      ref={buttonRef}
      whileTap={{ scale: 0.95 }}
      onMouseEnter={(e) => {
        setHovered(true)
        onMouseEnter?.(e)
      }}
      onTouchStart={() => setHovered(true)}
      onTouchEnd={() => setHovered(false)}
      onTouchCancel={() => setHovered(false)}
      onMouseLeave={(e) => {
        setHovered(false)
        onMouseLeave?.(e)
      }}
      className={classNames(styles.menuLink, className, {
        [styles.active]: active,
        // [styles.leftSpacing]: !Icon,
        [styles.highlighted]: highlighted,
      })}
      onClick={handleButtonClick}
      aria-label={label}
    >
      {Icon && (
        <AlertIconWrapper
          isActive={!!section.children?.some((child) => child.alertActive)}
          spaced
          success
        >
          <span className={styles.leftIcon}>
            <Icon size={24} color={iconColor} />
          </span>
        </AlertIconWrapper>
      )}
      <AnimatePresence>
        <Box flex overflowX='hidden' fullWidth minWidth='0'>
          <AlertIconWrapper isActive={alertActive} spaced success className={styles.labelWrap}>
            <Body2 className={styles.label}>
              <Truncate>{label}</Truncate>
            </Body2>
          </AlertIconWrapper>
          {infoClick && <ActionIcon icon={InfoIcon} size='x20' onClick={infoClick} />}
          {(screenChildren || showChevron) && (
            <motion.span
              key='chevron'
              className={styles.rightIcon}
              variants={iconVariants}
              initial='hidden'
              animate='visible'
              exit='exit'
              transition={{ duration: 0.2 }}
            >
              <ChevronIcon direction={chevronDirection} size={24} color={iconColor} />
            </motion.span>
          )}
          {RightIcon && (
            <motion.span
              key='rightIcon'
              className={styles.rightIcon}
              variants={iconVariants}
              initial='hidden'
              animate='visible'
              exit='exit'
              transition={{ duration: 0.2 }}
            >
              <RightIcon color={iconColor} size={24} />
            </motion.span>
          )}
        </Box>
      </AnimatePresence>
    </motion.button>
  )
}
